import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProdiSelect from "@/components/ProdiSelect";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, ClipboardList, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { seesAllProdi, isAdminKoas, bisaInputRotasi, istilahProdi, bolehKelolaSinkronStase } from "@/lib/roles";
import SinkronStaseKartu from "@/components/SinkronStaseKartu";
import { segmenSedangBerjalan, teksPeriodeSegmen } from "@/lib/segmenRotasi";
import StickyHeader from "@/components/StickyHeader";
import RotasiKlinikViewTabs from "@/components/RotasiKlinikViewTabs";
import RotasiInputCepatPanel from "@/components/RotasiInputCepatPanel";
import { pesertaBelumBerikutnya, statusIsiDariParam } from "@/lib/rotasiCepat";
import RotasiIsiMassalDialog from "@/components/RotasiIsiMassalDialog";
import { pilihSemuaTampil, semuaTampilTerpilih, statusPilihan, togglePilih } from "@/lib/isiMassal";

const BULAN_NAMA = [
    "", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];
const now = new Date();
const THIS_YEAR = now.getFullYear();
const YEAR_OPTIONS = [THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1];
// Baris per halaman utk tabel drilldown "Detail per Peserta", disamakan dgn
// PAGE_SIZE di halaman Peserta Didik (lihat pages/Peserta.jsx) supaya konsisten.
const PAGE_SIZE = 100;
const FILTER_STATUS = [
    { value: "semua", label: "Semua" },
    { value: "belum", label: "Belum Isi" },
    { value: "sudah", label: "Sudah Isi" },
];

// Menu monitoring "Rotasi Klinik" (tab "Per Peserta"): kepatuhan pengisian bulanan
// per prodi (sudah vs belum punya minimal 1 segmen rotasi bulan berjalan), plus
// drill-down per peserta -- termasuk instalasi & periode tiap segmennya, dan
// filter opsional ke satu Instalasi tertentu. Admin Prodi otomatis dibatasi ke
// prodinya sendiri oleh backend. Lihat juga tab "Per Instalasi" (RotasiKlinikPerInstalasi.jsx).
export default function RotasiKlinik() {
    const { user } = useAuth();
    const isSuper = seesAllProdi(user.role);
    const isKoas = isAdminKoas(user.role);
    const istilah = istilahProdi(user.role);
    const bisaInput = bisaInputRotasi(user.role);

    // ?bulan=&tahun= dari tautan Dashboard "Perlu dilengkapi" (bulan berjalan versi
    // server, WIB) -- tanpa itu, default bulan & tahun perangkat seperti sebelumnya.
    const [searchParams, setSearchParams] = useSearchParams();
    const [bulan, setBulan] = useState(() => searchParams.get("bulan") || String(now.getMonth() + 1));
    const [tahun, setTahun] = useState(() => searchParams.get("tahun") || String(THIS_YEAR));
    const [prodiList, setProdiList] = useState([]);
    const [prodiFilter, setProdiFilter] = useState("all");
    const [instalasiList, setInstalasiList] = useState([]);
    const [instalasiFilter, setInstalasiFilter] = useState("all");
    // Filter Semua/Belum Isi/Sudah Isi (?status=belum|sudah, dari tautan Dashboard juga).
    const [statusIsi, setStatusIsi] = useState(() => statusIsiDariParam(searchParams.get("status")));
    const [master, setMaster] = useState({ departemen: [], divisi: [], instalasi: [] });
    const [locked, setLocked] = useState(false);
    // Panel Input Rotasi Cepat: satu panel terbuka pada satu waktu (id peserta).
    const [openId, setOpenId] = useState(null);
    // Isi Massal: peserta terpilih (Map id -> {id, nama, npm}), dikosongkan saat filter/bulan berubah.
    const [pilihan, setPilihan] = useState(() => new Map());
    const [isiMassalBuka, setIsiMassalBuka] = useState(false);
    const [data, setData] = useState(null);
    const [pesertaTotal, setPesertaTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    // "Semua Prodi" (ribuan peserta) jauh lebih lambat drpd 1 prodi (puluhan) -- kalau
    // user ganti filter sebelum request lama selesai, respons yg lambat & sudah usang
    // bisa balik menimpa state meski bukan lagi filter yg aktif. Guard pakai id request
    // terbaru supaya hanya respons terakhir yg dipakai.
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!isSuper) return;
        (async () => {
            try { const { data } = await api.get("/prodi"); setProdiList(data); } catch {}
        })();
    }, [isSuper]);

    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/instalasi-klinik"); setInstalasiList(data); } catch {}
        })();
    }, []);

    useEffect(() => {
        if (!bisaInput) return;
        (async () => {
            try {
                const [dep, div, periode] = await Promise.all([
                    api.get("/departemen-klinik"), api.get("/divisi-klinik"), api.get("/periode-config"),
                ]);
                setMaster((m) => ({ ...m, departemen: dep.data, divisi: div.data }));
                setLocked(!!periode.data?.is_locked);
            } catch {
                toast.error("Gagal memuat master Departemen/Divisi");
            }
        })();
    }, [bisaInput]);
    useEffect(() => { setMaster((m) => ({ ...m, instalasi: instalasiList })); }, [instalasiList]);

    const gantiStatusIsi = (v) => {
        setStatusIsi(v);
        const sp = new URLSearchParams(searchParams);
        if (v === "semua") sp.delete("status"); else sp.set("status", v);
        setSearchParams(sp, { replace: true });
    };

    const load = async (pageArg) => {
        const targetPage = pageArg ?? page;
        const reqId = ++requestIdRef.current;
        setLoading(true);
        try {
            const params = { bulan, tahun, page: targetPage, page_size: PAGE_SIZE };
            if (isSuper && prodiFilter !== "all") params.prodi_id = prodiFilter;
            if (instalasiFilter !== "all") params.instalasi_id = instalasiFilter;
            if (statusIsi !== "semua") params.status_isi = statusIsi;
            const { data } = await api.get("/rotasi-klinik/monitoring", { params });
            if (reqId !== requestIdRef.current) return null; // respons request lama yg sudah usang, abaikan
            setData(data);
            setPesertaTotal(data.peserta_total ?? data.peserta.length);
            return data;
        } catch {
            if (reqId !== requestIdRef.current) return null;
            toast.error("Gagal memuat data monitoring Rotasi Klinik");
            return null;
        } finally {
            if (reqId === requestIdRef.current) setLoading(false);
        }
    };
    // Filter berubah -> selalu kembali ke halaman 1 (jangan pakai state `page` yg
    // lama di request ini, supaya tidak sempat fetch dgn kombinasi page+filter basi).
    useEffect(() => { setOpenId(null); setPilihan(new Map()); setPage(1); load(1); /* eslint-disable-line */ }, [bulan, tahun, prodiFilter, instalasiFilter, statusIsi]);

    const totalPages = Math.max(1, Math.ceil(pesertaTotal / PAGE_SIZE));
    const goToPage = (p) => {
        const clamped = Math.min(Math.max(1, p), totalPages);
        if (clamped === page) return;
        setOpenId(null);
        setPage(clamped);
        load(clamped);
    };

    // Setelah simpan: muat ulang (status baris & kartu ikut segar). "Simpan & berikutnya" membuka
    // peserta Belum Isi berikutnya pada daftar yang sedang tampil.
    const setelahSimpan = async (lanjut) => {
        const berikut = lanjut && data ? pesertaBelumBerikutnya(data.peserta, openId) : null;
        const segar = await load(page);
        const ada = berikut && segar?.peserta.some((p) => p.id === berikut);
        setOpenId(ada ? berikut : null);
        if (lanjut && !ada) toast.info("Tidak ada lagi peserta Belum Isi di halaman ini");
        if (ada) setTimeout(() => document.getElementById(`rk-nama-${berikut}`)?.focus(), 0);
    };
    const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));
    const setelahIsiMassal = async () => {
        setIsiMassalBuka(false);
        setPilihan(new Map());
        setOpenId(null);
        await load(page);
    };

    // Saran Nama RS (belum ada master RS Jejaring): nama RS Jejaring yang sudah tercatat di daftar ini.
    const saranRs = useMemo(() => {
        const nama = new Set();
        (data?.peserta || []).forEach((p) => p.segments.forEach((s) => {
            if (s.jenis === "RS Jejaring" && s.instalasi_nama && s.instalasi_nama !== "-") nama.add(s.instalasi_nama);
        }));
        return [...nama].sort((a, b) => a.localeCompare(b, "id"));
    }, [data]);

    const totals = useMemo(() => {
        if (!data) return { total: 0, sudah: 0, belum: 0 };
        return data.per_prodi.reduce((acc, r) => ({
            total: acc.total + r.total_aktif, sudah: acc.sudah + r.sudah_isi, belum: acc.belum + r.belum_isi,
        }), { total: 0, sudah: 0, belum: 0 });
    }, [data]);

    // Drilldown (tabel per peserta) tampil kalau backend mengirim baris -- itu terjadi
    // saat prodi/instalasi difilter, atau otomatis utk role admin_prodi. Dicek dari
    // pesertaTotal (bukan data.peserta.length) supaya tetap benar walau halaman
    // saat ini bukan halaman 1.
    // Dgn filter Belum/Sudah Isi, daftar boleh kosong -- tetap tampil (supaya filter bisa diganti)
    // bila server memang mengirim daftar peserta (cermin show_peserta di backend).
    const drilldownDiharapkan = !isSuper || prodiFilter !== "all" || instalasiFilter !== "all";
    const showDrilldown = data && (pesertaTotal > 0 || (statusIsi !== "semua" && drilldownDiharapkan));
    // Kotak centang Isi Massal: hak yg sama dgn panel Input Rotasi Cepat, dan hanya saat jendela edit terbuka.
    const pakaiPilih = bisaInput && !locked;
    const kolom = pakaiPilih ? 5 : 4;
    const statusIsiMassal = statusPilihan(pilihan.size, data?.aturan_rotasi?.maks_isi_massal);
    const semuaTampil = data ? semuaTampilTerpilih(pilihan, data.peserta) : false;

    return (
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Monitoring</div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                            Rotasi Klinik
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            Kepatuhan pengisian rotasi klinis bulanan {isSuper ? "seluruh prodi" : isKoas && !user.prodi_id ? "Peserta Koas (semua modul Koas)" : `${istilah.kecil} Anda`} (peserta status Aktif).
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {isSuper && (
                            <ProdiSelect
                                prodi={prodiList}
                                value={prodiFilter}
                                onValueChange={setProdiFilter}
                                includeAll
                                placeholder="Prodi"
                                className="w-56 h-10"
                                testId="rk-monitor-prodi-select"
                            />
                        )}
                        <Select value={instalasiFilter} onValueChange={setInstalasiFilter}>
                            <SelectTrigger className="w-56 h-10" data-testid="rk-monitor-instalasi-select"><SelectValue placeholder="Instalasi" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Semua Instalasi</SelectItem>
                                {instalasiList.map((i) => <SelectItem key={i.id} value={i.id}>{i.nama}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={bulan} onValueChange={setBulan}>
                            <SelectTrigger className="w-36 h-10" data-testid="rk-monitor-bulan-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {BULAN_NAMA.slice(1).map((b, i) => <SelectItem key={i + 1} value={String(i + 1)}>{b}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={tahun} onValueChange={setTahun}>
                            <SelectTrigger className="w-24 h-10" data-testid="rk-monitor-tahun-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {YEAR_OPTIONS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="mt-3">
                    <RotasiKlinikViewTabs active="peserta" />
                </div>
            </StickyHeader>

            {bolehKelolaSinkronStase(user.role) && <SinkronStaseKartu role={user.role} />}

            {loading ? (
                <div className="p-10 text-slate-500">Memuat data...</div>
            ) : !data ? (
                <div className="p-10 text-slate-500">Tidak ada data.</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-[#267B84] text-white flex items-center justify-center shrink-0">
                                    <ClipboardList className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Peserta Aktif Wajib Isi</div>
                                    <div className="text-2xl font-extrabold text-slate-900">{totals.total}</div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
                                    <CheckCircle2 className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Sudah Isi</div>
                                    <div className="text-2xl font-extrabold text-emerald-700">{totals.sudah}</div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-rose-600 text-white flex items-center justify-center shrink-0">
                                    <XCircle className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Belum Isi</div>
                                    <div className="text-2xl font-extrabold text-rose-700">{totals.belum}</div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <p className="text-xs text-slate-500 -mt-2" data-testid="rk-tidak-wajib">
                        Peserta ber-Stase MDU-MDK, Penelitian, atau Belum Stase, dan peserta yang seluruh bulan terpilih tercakup
                        periode cuti, tidak dihitung wajib: {data.tidak_wajib_total ?? 0} peserta
                        (tidak masuk angka di atas; tetap tampil di daftar "Semua" dan tetap bisa diisi rotasinya).
                    </p>

                    {!showDrilldown && (
                        <Card className="border-slate-200 shadow-sm overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full compact">
                                    <thead className="bg-slate-100">
                                        <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                            <th>{istilah.panjang}</th><th>Jenjang</th><th>Aktif Wajib Isi</th><th>Sudah Isi</th><th>Belum Isi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.per_prodi.length === 0 ? (
                                            <tr><td colSpan={5} className="text-center py-10 text-slate-400">Belum ada peserta aktif.</td></tr>
                                        ) : data.per_prodi.map((r) => (
                                            <tr key={r.prodi_id} className="border-t border-slate-200 hover:bg-slate-50">
                                                <td className="font-semibold text-slate-800">{r.nama_prodi}</td>
                                                <td><Badge variant="outline" className="text-[10px]">{r.jenjang}</Badge></td>
                                                <td className="text-slate-600">{r.total_aktif}</td>
                                                <td className="text-emerald-700 font-semibold">{r.sudah_isi}</td>
                                                <td className={r.belum_isi > 0 ? "text-rose-700 font-semibold" : "text-slate-400"}>{r.belum_isi}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    )}

                    {showDrilldown && (
                        <Card className="border-slate-200 shadow-sm overflow-hidden">
                            <div className="px-4 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-600 flex flex-wrap items-center justify-between gap-2">
                                <span>
                                    Detail per Peserta — {BULAN_NAMA[Number(bulan)]} {tahun}
                                    {instalasiFilter !== "all" && (
                                        <> · {instalasiList.find((i) => i.id === instalasiFilter)?.nama || "Instalasi"}</>
                                    )}
                                </span>
                                <div className="flex gap-1" role="group" aria-label="Filter status pengisian" data-testid="rk-filter-status">
                                    {FILTER_STATUS.map((f) => (
                                        <Button
                                            key={f.value} type="button" size="sm"
                                            variant={statusIsi === f.value ? "default" : "outline"}
                                            aria-pressed={statusIsi === f.value}
                                            className={`h-7 px-2.5 text-xs ${statusIsi === f.value ? "bg-[#267B84] hover:bg-[#267B84] text-white" : ""}`}
                                            onClick={() => gantiStatusIsi(f.value)}
                                            data-testid={`rk-filter-status-${f.value}`}
                                        >
                                            {f.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                            {bisaInput && (
                                <div className="px-4 py-1.5 border-b text-[11px] text-slate-500">
                                    Klik nama peserta untuk menambah rotasi bulan ini langsung di bawah barisnya.
                                    {pakaiPilih && " Centang beberapa peserta lalu klik \"Isi Massal\" untuk rotasi yang sama sekaligus."}
                                </div>
                            )}
                            {pakaiPilih && (
                                <div className="px-4 py-2 border-b flex flex-wrap items-center gap-2" data-testid="im-bar">
                                    <Button
                                        type="button" size="sm" className="h-8 bg-[#00B9AD] hover:bg-[#60C0D0]"
                                        disabled={!statusIsiMassal.bisa} onClick={() => setIsiMassalBuka(true)} data-testid="im-buka"
                                    >
                                        Isi Massal{pilihan.size ? ` (${pilihan.size})` : ""}
                                    </Button>
                                    {pilihan.size > 0 && (
                                        <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setPilihan(new Map())}
                                            data-testid="im-kosongkan">
                                            Kosongkan pilihan
                                        </Button>
                                    )}
                                    {statusIsiMassal.pesan && (
                                        <span className="text-xs text-rose-700" role="alert" data-testid="im-pesan-batas">{statusIsiMassal.pesan}</span>
                                    )}
                                </div>
                            )}
                            <div className="overflow-x-auto">
                                <table className="w-full compact">
                                    <thead className="bg-slate-100">
                                        <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                            {pakaiPilih && (
                                                <th className="w-8">
                                                    <input
                                                        type="checkbox" className="h-4 w-4 accent-[#00B9AD] cursor-pointer" checked={semuaTampil}
                                                        onChange={(e) => setPilihan((m) => pilihSemuaTampil(m, data.peserta, e.target.checked))}
                                                        aria-label="Pilih semua peserta yang tampil" data-testid="im-pilih-semua"
                                                    />
                                                </th>
                                            )}
                                            <th>Nama</th><th>NPM</th><th>Status Pengisian</th><th>Instalasi &amp; Periode</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.peserta.length === 0 && (
                                            <tr><td colSpan={kolom} className="text-center py-10 text-slate-400">Tidak ada peserta untuk filter ini.</td></tr>
                                        )}
                                        {data.peserta.map((p) => [
                                            <tr
                                                key={p.id}
                                                className={`border-t border-slate-200 hover:bg-slate-50 ${bisaInput ? "cursor-pointer" : ""} ${openId === p.id ? "bg-slate-50" : ""}`}
                                                onClick={bisaInput ? () => toggle(p.id) : undefined}
                                                data-testid={`rk-row-${p.id}`}
                                            >
                                                {pakaiPilih && (
                                                    <td className="align-top" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox" className="h-4 w-4 accent-[#00B9AD] cursor-pointer mt-0.5"
                                                            checked={pilihan.has(p.id)} onChange={() => setPilihan((m) => togglePilih(m, p))}
                                                            aria-label={`Pilih ${p.nama} untuk Isi Massal`} data-testid={`im-pilih-${p.id}`}
                                                        />
                                                    </td>
                                                )}
                                                <td className="font-semibold text-slate-800 align-top">
                                                    {bisaInput ? (
                                                        <button
                                                            type="button" id={`rk-nama-${p.id}`}
                                                            className="inline-flex items-center gap-1 text-left font-semibold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00B9AD] rounded"
                                                            aria-expanded={openId === p.id} aria-controls={`rk-panel-${p.id}`}
                                                            onClick={(e) => { e.stopPropagation(); toggle(p.id); }}
                                                        >
                                                            <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${openId === p.id ? "rotate-180" : ""}`} />
                                                            {p.nama}
                                                        </button>
                                                    ) : p.nama}
                                                </td>
                                                <td className="font-mono text-xs align-top">{p.npm}</td>
                                                <td className="align-top">
                                                    {p.has_rotasi ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold bg-emerald-100 text-emerald-700 border-emerald-300">
                                                            <CheckCircle2 className="w-3 h-3" /> Sudah Isi
                                                        </span>
                                                    ) : p.wajib_rotasi === false ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold bg-slate-100 text-slate-600 border-slate-300">
                                                            {`Tidak wajib (${p.alasan_tidak_wajib || "MDU-MDK / Penelitian / Belum Stase / Cuti"})`}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold bg-rose-100 text-rose-700 border-rose-300">
                                                            <XCircle className="w-3 h-3" /> Belum Isi
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-xs text-slate-600 align-top">
                                                    {p.segments.length === 0 ? (
                                                        <span className="text-slate-400">-</span>
                                                    ) : (
                                                        <div className="space-y-1">
                                                            {p.segments.map((s, i) => (
                                                                <div key={i} className="flex items-center gap-1.5">
                                                                    <span className="font-semibold text-slate-700">{s.jenis}</span>
                                                                    <span className="text-slate-400">—</span>
                                                                    <span>{s.instalasi_nama || "-"}</span>
                                                                    {teksPeriodeSegmen(s) && (
                                                                        <span className="text-slate-400">({teksPeriodeSegmen(s)})</span>
                                                                    )}
                                                                    {segmenSedangBerjalan(s) && (
                                                                        <Badge className="bg-[#CDDC29] text-[#0F2E30] hover:bg-[#CDDC29] text-[10px] px-1.5 py-0">
                                                                            Sedang Berjalan
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>,
                                            bisaInput && openId === p.id && (
                                                <tr key={`${p.id}-panel`} id={`rk-panel-${p.id}`}>
                                                    <td colSpan={kolom} className="p-0">
                                                        {/* w-0 min-w-full: lebar panel tidak ikut melebarkan tabel; grid di dalamnya scroll sendiri */}
                                                        <div className="w-0 min-w-full">
                                                            <RotasiInputCepatPanel
                                                                pesertaId={p.id} bulan={Number(bulan)} tahun={Number(tahun)}
                                                                master={master} saranRs={saranRs} locked={locked}
                                                                onTersimpan={setelahSimpan}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ),
                                        ])}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
                                <span>
                                    Menampilkan <span className="font-bold text-slate-800">{data.peserta.length}</span> dari{" "}
                                    <span className="font-bold text-slate-800">{pesertaTotal}</span> peserta didik
                                </span>
                                <div className="flex items-center gap-2" data-testid="rk-peserta-pagination">
                                    <Button
                                        size="sm" variant="outline" className="h-7 px-2"
                                        disabled={page <= 1 || loading}
                                        onClick={() => goToPage(page - 1)}
                                        data-testid="rk-peserta-page-prev"
                                    >
                                        <ChevronLeft className="w-3.5 h-3.5" />
                                    </Button>
                                    <span className="text-slate-600">Halaman {page} dari {totalPages}</span>
                                    <Button
                                        size="sm" variant="outline" className="h-7 px-2"
                                        disabled={page >= totalPages || loading}
                                        onClick={() => goToPage(page + 1)}
                                        data-testid="rk-peserta-page-next"
                                    >
                                        <ChevronRight className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    )}
                </>
            )}
            {pakaiPilih && data && (
                <RotasiIsiMassalDialog
                    open={isiMassalBuka} onOpenChange={setIsiMassalBuka} peserta={[...pilihan.values()]}
                    bulan={Number(bulan)} tahun={Number(tahun)} master={master} saranRs={saranRs}
                    aturan={data.aturan_rotasi} onTersimpan={setelahIsiMassal} labelProdi={istilah.singkat}
                />
            )}
        </div>
    );
}
